/**
 * DebugControlsWidget - Reusable debug controls widget
 *
 * Provides a flexible debug widget that can display various controls like
 * checkboxes, buttons, sliders, etc.
 */

import { DebugWidget, createWidgetContainer } from './DebugOverlay';

export interface CheckboxControl {
  type: 'checkbox';
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export interface ButtonControl {
  type: 'button';
  label: string;
  onClick: () => void;
}

export type DebugControl = CheckboxControl | ButtonControl;

export class DebugControlsWidget implements DebugWidget {
  name: string;
  container: HTMLElement;
  private contentContainer: HTMLElement;
  private controls: Map<string, { element: HTMLElement; control: DebugControl }> = new Map();

  constructor(name: string) {
    this.name = name;
    this.container = createWidgetContainer(name);

    // Create content container for controls
    this.contentContainer = document.createElement('div');
    this.contentContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      gap: 4px;
    `;
    this.container.appendChild(this.contentContainer);
  }

  /**
   * Add a control to the widget
   */
  addControl(id: string, control: DebugControl): void {
    if (this.controls.has(id)) {
      console.warn(`[DebugControlsWidget] Control "${id}" already exists`);
      return;
    }

    const controlElement = this.createControlElement(control);
    this.contentContainer.appendChild(controlElement);
    this.controls.set(id, { element: controlElement, control });
  }

  /**
   * Remove a control from the widget
   */
  removeControl(id: string): void {
    const entry = this.controls.get(id);
    if (entry) {
      this.contentContainer.removeChild(entry.element);
      this.controls.delete(id);
    }
  }

  /**
   * Update a control's state
   */
  updateControl(id: string, updates: Partial<DebugControl>): void {
    const entry = this.controls.get(id);
    if (!entry) return;

    Object.assign(entry.control, updates);

    // Re-create the control element with updated state
    const newElement = this.createControlElement(entry.control);
    this.contentContainer.replaceChild(newElement, entry.element);
    entry.element = newElement;
  }

  /**
   * Clear all controls
   */
  clearControls(): void {
    this.contentContainer.innerHTML = '';
    this.controls.clear();
  }

  private createControlElement(control: DebugControl): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.style.cssText = `
      padding: 6px 0;
      display: flex;
      align-items: center;
      gap: 8px;
    `;

    if (control.type === 'checkbox') {
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = control.checked;
      checkbox.style.cssText = `
        cursor: pointer;
        width: 14px;
        height: 14px;
      `;
      checkbox.addEventListener('change', () => {
        control.onChange(checkbox.checked);
      });

      const label = document.createElement('label');
      label.textContent = control.label;
      label.style.cssText = `
        cursor: pointer;
        user-select: none;
        flex: 1;
      `;
      label.addEventListener('click', () => {
        checkbox.checked = !checkbox.checked;
        control.onChange(checkbox.checked);
      });

      wrapper.appendChild(checkbox);
      wrapper.appendChild(label);
    } else if (control.type === 'button') {
      const button = document.createElement('button');
      button.textContent = control.label;
      button.style.cssText = `
        padding: 4px 12px;
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 4px;
        color: #fff;
        cursor: pointer;
        font-family: inherit;
        font-size: inherit;
        width: 100%;
      `;
      button.addEventListener('click', control.onClick);
      button.addEventListener('mouseenter', () => {
        button.style.background = 'rgba(255, 255, 255, 0.2)';
      });
      button.addEventListener('mouseleave', () => {
        button.style.background = 'rgba(255, 255, 255, 0.1)';
      });

      wrapper.appendChild(button);
    }

    return wrapper;
  }

  update(): void {
    // No automatic updates needed for controls
  }

  destroy(): void {
    this.clearControls();
    this.container.remove();
  }
}
